// src/frontend/src/pages/OtpSetupPage.js
import { Box, Container, Typography, Paper, Button, Alert, useTheme } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const INSTALLER_SHA256 = 'd386abf8c5e356fc2a92ab01ea414ce3a858b2998b77faf887dc8ecb661706b0';

const CodeBlock = ({ children }) => (
    <Box
        component="pre"
        sx={{
            bgcolor: 'var(--background-color-dark)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 2,
            overflowX: 'auto',
            fontSize: '0.85rem',
            fontFamily: 'monospace',
            mb: 2
        }}
    >
        {children}
    </Box>
);

const OtpSetupPage = () => {
    const mode = useTheme().palette.mode;
    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                    SMS Code Autofill Setup
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    For the StockMaid Chrome extension on macOS
                </Typography>

                <Box sx={{ '& h2': { mt: 4, mb: 2 }, '& p': { mb: 2, lineHeight: 1.8 } }}>
                    <Typography variant="body1">
                        When the checkout assistant runs, Best Buy sometimes texts a verification code
                        to the phone number on your account. With this optional setup, the extension
                        reads that code from the Messages app on your Mac and types it in for you, so
                        you never have to reach for your phone during a drop.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Everything runs locally. The reader looks only at messages from Best Buy's
                        short code on your own Mac, and the code never leaves your machine. If you
                        skip this setup, the assistant simply pauses and waits for you to type the
                        code yourself.
                    </Alert>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        Before you start
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">A Mac that receives your text messages. On your iPhone, enable Settings, Messages, Text Message Forwarding for your Mac.</Typography></li>
                        <li><Typography variant="body1">Node.js 18 or newer. Check with <code>node --version</code>.</Typography></li>
                        <li><Typography variant="body1">The StockMaid extension installed from the Chrome Web Store.</Typography></li>
                    </Box>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        1. Download the installer
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        href={`/downloads/stockmaid-otp-installer.zip?v=${INSTALLER_SHA256.slice(0, 8)}`}
                        sx={{
                            bgcolor: 'var(--sm-btn-contained-bg)',
                            color: 'var(--sm-btn-contained-text)',
                            fontWeight: 'bold',
                            mb: 1,
                            '&:hover': { bgcolor: 'var(--sm-btn-contained-hover)' }
                        }}
                    >
                        Download installer (14 KB)
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
                        SHA-256: {INSTALLER_SHA256}
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        2. Run it
                    </Typography>
                    <Typography variant="body1">
                        Unzip the download, open Terminal in that folder, and run:
                    </Typography>
                    <CodeBlock>{'./install_native_host.sh eagpnnacaeonojmmkgnmnbgddfikfpfk'}</CodeBlock>
                    <Typography variant="body1">
                        The script installs a small reader at <code>/usr/local/bin/stockmaid-sms-reader</code> and
                        registers it with Chrome so only the StockMaid extension can talk to it.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        3. Grant Full Disk Access to Node
                    </Typography>
                    <Typography variant="body1">
                        macOS protects the Messages database, so the reader needs permission: open
                        System Settings, Privacy and Security, Full Disk Access, and add your{' '}
                        <code>node</code> binary (the installer prints its location). Then quit and
                        reopen Chrome.
                    </Typography>

                    <Box
                        component="img"
                        src={`/images/docs/fda-${mode === 'dark' ? 'dark' : 'light'}.png`}
                        alt="macOS Full Disk Access settings with both node entries enabled"
                        sx={{
                            display: 'block',
                            maxWidth: 420,
                            width: '100%',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            my: 2
                        }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        A correct setup: both node entries enabled. Other apps in your list will differ.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        4. Fill in your Best Buy details
                    </Typography>
                    <Typography variant="body1">
                        In the extension popup, open Settings, then Advanced, and enter your Best Buy
                        account email, last name, and the last four digits of your phone number. The
                        assistant uses these to pass Best Buy's identity check before the code is
                        requested. With all three filled in, autofill is on. Leave them empty and the
                        assistant waits for you to type codes manually instead.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        Troubleshooting
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Codes not filling: confirm a Best Buy text actually arrives in the Messages app on the Mac. If not, revisit Text Message Forwarding.</Typography></li>
                        <li><Typography variant="body1">Permission errors in the assistant status: Full Disk Access is missing for node. Add it and restart Chrome.</Typography></li>
                        <li><Typography variant="body1">Just installed: Chrome only picks up new native hosts at launch. Quit Chrome fully and reopen it.</Typography></li>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default OtpSetupPage;
